<script lang="ts">
  import { TextCorrectionService } from '$lib/services/TextCorrectionService'
  import { EditorStateService } from '$lib/services/EditorStateService'
  import { grammarActions } from '$lib/stores/grammar'
  import { aiFeaturesStore } from '$lib/stores/aiFeatures'
  import { fileStore, activeTab } from '$lib/stores/fileStore'
  import { get } from 'svelte/store'
  import { useCustomEvent } from '$lib/hooks/useEventListener'
  import type { AppliedCorrectionInfo } from '$lib/services/UndoRedoService'
  
  /**
   * TextEditingController: Handles text editing and history management
   * Single Responsibility: Text state management, undo/redo, and history tracking
   */
  
  interface Props {
    editorText: string
    errorState: any
    hasChecked: boolean
    correctionStates: Map<string, AppliedCorrectionInfo>
    textManager: any
    showSplitEditor: boolean
    onTextChange: (text: string, isCorrection?: boolean) => void
    onUndoRedo: (entry: any) => void
    onAISegmentsUpdate: (segments: any[]) => void
  }
  
  let {
    editorText = $bindable(''),
    errorState,
    hasChecked = $bindable(false),
    correctionStates = $bindable(new Map()),
    textManager,
    showSplitEditor = $bindable(false),
    onTextChange,
    onUndoRedo,
    onAISegmentsUpdate
  }: Props = $props()
  
  let previousText = $state('')
  let isCorrectingError = $state(false)
  let isUndoRedoOperation = $state(false)
  let historyTimer: ReturnType<typeof setTimeout> | null = null
  let lastSavedText = $state('')
  let textChangeCount = $state(0)
  let aiSegments = $state<any[]>([])
  
  // Get current active tab ID
  function getCurrentTabId(): string | null {
    const tab = get(activeTab)
    return tab?.id || null
  }
  
  // Initialize lastSavedText when editorText is first set
  let isInitialized = false
  $effect(() => {
    if (!isInitialized && editorText && editorText.length > 0) {
      lastSavedText = editorText
      isInitialized = true
    }
  })
  
  // Reset initialization when tab changes
  $effect(() => {
    const tabId = getCurrentTabId()
    if (tabId) {
      // Tab changed, reset initialization
      return () => {
        isInitialized = false
        lastSavedText = ''
        textChangeCount = 0
      }
    }
  })
  
  // Handle text changes - update errors automatically
  $effect(() => {
    if (editorText !== undefined && editorText !== previousText) {
      const tabId = getCurrentTabId()
      
      // Don't add to history if we're in an undo/redo operation
      if (!isUndoRedoOperation && tabId) {
        textChangeCount++
        
        // Clear existing timer
        if (historyTimer) {
          clearTimeout(historyTimer)
        }
        
        if (!isCorrectingError && editorText !== lastSavedText) {
          // Add to history based on conditions:
          // 1. After significant text change (more than 10 characters difference)
          // 2. After a pause in typing (1 second)
          // 3. After multiple small changes (every 5 changes)
          const significantChange = Math.abs(editorText.length - lastSavedText.length) > 10
          const multipleChanges = textChangeCount >= 5
          
          if (significantChange || multipleChanges) {
            // Save immediately for significant changes
            const currentTabId = getCurrentTabId()
            if (currentTabId) {
              fileStore.addToHistory(currentTabId, editorText)
              lastSavedText = editorText
              textChangeCount = 0
              
              // Notify that history was updated
              window.dispatchEvent(new CustomEvent('history:updated', { detail: { tabId: currentTabId } }))
            }
          } else {
            // Debounce for small changes
            historyTimer = setTimeout(() => {
              if (isCorrectingError) return
              
              const currentTabId = getCurrentTabId()
              if (currentTabId && editorText !== lastSavedText) {
                fileStore.addToHistory(currentTabId, editorText)
                lastSavedText = editorText
                textChangeCount = 0
                
                // Notify that history was updated
                window.dispatchEvent(new CustomEvent('history:updated', { detail: { tabId: currentTabId } }))
              }
            }, 1000) // 1 second debounce for normal typing
          }
        }
      }
      
      // Only reset hasChecked if user is typing new text, not when applying corrections
      if (!isCorrectingError && !isUndoRedoOperation) {
        hasChecked = false
      }
      
      // Clear errors and AI suggestions if text is empty
      if (!editorText || editorText.trim() === '') {
        EditorStateService.clearErrors()
        hasChecked = false
        previousText = editorText
        return
      }
      
      // Update error positions and filter out invalid ones
      // Skip if this is a correction operation (errors are being handled by CorrectionController)
      if (errorState.errors.length > 0 && !isCorrectingError) {
        const updatedErrors = TextCorrectionService.updateErrorsForText(errorState.errors, editorText)
        // 텍스트 변경 시 기존 오류를 업데이트할 때는 AI 병합 비활성화
        // 이미 있는 오류들의 위치만 업데이트하는 것이므로
        grammarActions.setErrors(updatedErrors, false)
      }
      
      previousText = editorText
      const wasCorrectingError = isCorrectingError
      isCorrectingError = false
      isUndoRedoOperation = false
      
      // Notify parent of text change, passing correction flag
      onTextChange(editorText, wasCorrectingError)
    }
  })
  
  // Undo event listener - simplified for tab-specific history
  useCustomEvent<{ text: string }>('editor:undo', (data) => {
    if (data?.text !== undefined) {
      isUndoRedoOperation = true
      isCorrectingError = true
      
      // Restore text
      editorText = data.text
      textManager?.setText(data.text)
      
      // Clear errors when undoing
      grammarActions.setErrors([], false)
      hasChecked = false
      
      // Reset flags after a short delay
      setTimeout(() => {
        isUndoRedoOperation = false
        isCorrectingError = false
      }, 100)
      
      onUndoRedo(data)
    }
  })
  
  // Redo event listener - simplified for tab-specific history
  useCustomEvent<{ text: string }>('editor:redo', (data) => {
    if (data?.text !== undefined) {
      isUndoRedoOperation = true
      isCorrectingError = true
      
      // Restore text
      editorText = data.text
      textManager?.setText(data.text)
      
      // Clear errors when redoing
      grammarActions.setErrors([], false)
      hasChecked = false
      
      // Reset flags after a short delay
      setTimeout(() => {
        isUndoRedoOperation = false
        isCorrectingError = false
      }, 100)
      
      onUndoRedo(data)
    }
  })
  
  // Export methods for parent
  export function updateText(text: string, isManual: boolean = false, isCorrection: boolean = false) {
    // Clear any pending history timer
    if (isManual && historyTimer) {
      clearTimeout(historyTimer)
      historyTimer = null
    }
    
    // Mark as correction to prevent error position updates
    if (isCorrection) {
      isCorrectingError = true
    }
    
    editorText = text
    textManager?.setText(text)
    
    // Add to history if manual update
    if (isManual && !isCorrection) {
      const tabId = getCurrentTabId()
      if (tabId) {
        fileStore.addToHistory(tabId, text)
        lastSavedText = text
        textChangeCount = 0
        
        // Notify that history was updated
        window.dispatchEvent(new CustomEvent('history:updated', { detail: { tabId } }))
      }
    }
  }
  
  export function setIsCorrectingError(value: boolean) {
    isCorrectingError = value
  }
  
  export function addToHistory(type: string, description: string, previousText?: string) {
    // Clear any pending history timer
    if (historyTimer) {
      clearTimeout(historyTimer)
      historyTimer = null
    }
    
    // Add to tab-specific history
    const tabId = getCurrentTabId()
    if (tabId) {
      fileStore.addToHistory(tabId, editorText)
    }
  }
</script>